import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  RefreshCw,
  Database,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { ZohoTicketSyncRequest, TicketCategory } from "../types/tickets";
import { ticketService } from "../services/ticketService";

interface TicketSyncModalProps {
  onClose: () => void;
  onStartSync: (request: ZohoTicketSyncRequest) => void;
}

const TicketSyncModal: React.FC<TicketSyncModalProps> = ({
  onClose,
  onStartSync,
}) => {
  const [syncType, setSyncType] = useState<
    "incremental" | "full" | "date_range" | "latest_50"
  >("incremental");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [forceReprocess, setForceReprocess] = useState(false);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Set default date range for date_range sync type
    if (syncType === "date_range" && !startDate && !endDate) {
      const today = new Date();
      const lastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate()
      );

      setStartDate(lastMonth.toISOString().split("T")[0]);
      setEndDate(today.toISOString().split("T")[0]);
    }
  }, [syncType, startDate, endDate]);

  useEffect(() => {
    // Fetch categories for reference
    const fetchCategories = async () => {
      try {
        const response = await ticketService.getTicketCategories();
        setCategories(response.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const request: ZohoTicketSyncRequest = {
      sync_type: syncType,
      force_reprocess: forceReprocess,
    };

    if (syncType === "date_range") {
      request.start_date = startDate;
      request.end_date = endDate;
    }

    try {
      await onStartSync(request);
    } finally {
      setLoading(false);
    }
  };

  const getSyncDescription = (type: string) => {
    switch (type) {
      case "incremental":
        return "Sync tickets received in the last 15 days (closed tickets only). Recommended for regular updates.";
      case "full":
        return "Sync all tickets received in the last 90 days. Use for initial setup or comprehensive refresh.";
      case "date_range":
        return "Sync closed tickets within a specific date range. Useful for targeted updates.";
      case "latest_50":
        return "Sync the latest 50 closed tickets from Zoho Desk. Perfect for comprehensive testing or recent issues.";
      default:
        return "";
    }
  };

  const getEstimatedTime = (type: string) => {
    switch (type) {
      case "incremental":
        return "~3-10 minutes";
      case "full":
        return "~15 minutes - 1 hour";
      case "date_range":
        return "Varies by date range";
      case "latest_50":
        return "~5-15 minutes";
      default:
        return "";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
              Start Ticket Sync
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Synchronize tickets from Zoho Desk and process them with AI
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Sync Type Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Sync Type
            </label>
            <div className="space-y-3">
              {[
                {
                  value: "incremental",
                  icon: RefreshCw,
                  label: "Incremental Sync",
                },
                { value: "full", icon: Database, label: "Full Sync" },
                {
                  value: "date_range",
                  icon: Calendar,
                  label: "Date Range Sync",
                },
                {
                  value: "latest_50",
                  icon: Clock,
                  label: "Latest 50 Tickets",
                },
              ].map(({ value, icon: Icon, label }) => (
                <label
                  key={value}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="syncType"
                    value={value}
                    checked={syncType === value}
                    onChange={(e) => setSyncType(e.target.value as any)}
                    className="mt-1 text-orange-500 focus:ring-orange-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-neutral-500" />
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">
                        {label}
                      </span>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                        {getEstimatedTime(value)}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {getSyncDescription(value)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range Selection */}
          {syncType === "date_range" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
              Options
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceReprocess}
                  onChange={(e) => setForceReprocess(e.target.checked)}
                  className="text-orange-500 focus:ring-orange-500 rounded"
                />
                <div>
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    Force Reprocess
                  </span>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Reprocess tickets that have already been processed
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Warning for Full Sync */}
          {syncType === "full" && (
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                  Full Sync Warning
                </h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  Full sync will process all closed tickets from the last 3
                  months. This may take some time but is limited to recent
                  tickets for efficiency.
                </p>
              </div>
            </div>
          )}

          {/* Closed Tickets Info */}
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">
              Closed Tickets Only
            </h4>
            <p className="text-sm text-green-700 dark:text-green-400">
              All sync operations will only process closed tickets to focus on
              resolved issues and completed support cases for better knowledge
              base quality.
            </p>
          </div>

          {/* Current Categories Info */}
          {categories.length > 0 && (
            <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                Current Categories ({categories.length})
              </h4>
              <div className="flex flex-wrap gap-2 text-xs">
                {categories.slice(0, 8).map((category) => (
                  <span
                    key={category.name}
                    className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded"
                  >
                    {category.name} ({category.count})
                  </span>
                ))}
                {categories.length > 8 && (
                  <span className="px-2 py-1 bg-neutral-200 dark:bg-neutral-600 text-neutral-600 dark:text-neutral-400 rounded">
                    +{categories.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-lg transition-colors"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Start Sync
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketSyncModal;
